import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
  ListSubheader,
  InputBase,
  Hidden,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import DashboardIcon from '@material-ui/icons/Dashboard';
import PeopleIcon from '@material-ui/icons/People';
import BusinessIcon from '@material-ui/icons/Business';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import MapIcon from '@material-ui/icons/Map';
import FlightIcon from '@material-ui/icons/Flight';
import BookIcon from '@material-ui/icons/Book';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import MoneyIcon from '@material-ui/icons/Money';
import PaymentIcon from '@material-ui/icons/Payment';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import SearchIcon from '@material-ui/icons/Search';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 264;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: 'none',
  },
  toolbarRow: {
    minHeight: 68,
    gap: theme.spacing(2),
  },
  menuButton: {
    color: '#fff',
    [theme.breakpoints.up('md')]: { display: 'none' },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    color: '#fff',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    fontSize: '1.05rem',
  },
  brandIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  search: {
    position: 'relative',
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
    marginLeft: theme.spacing(2),
    width: '100%',
    maxWidth: 320,
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  searchIcon: {
    padding: theme.spacing(0, 1.5),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.85)',
  },
  searchInput: {
    color: 'inherit',
    width: '100%',
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)}px)`,
    fontSize: '0.9rem',
  },
  spacer: { flexGrow: 1 },
  iconWhite: { color: '#fff' },
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  drawerToolbar: {
    minHeight: 68,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
  },
  navSubheader: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: '36px',
    paddingLeft: theme.spacing(2),
    backgroundColor: 'transparent',
  },
  navItemIcon: {
    minWidth: 38,
    color: theme.palette.text.secondary,
  },
  navItemText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.92rem',
      fontWeight: 500,
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(4),
    minHeight: '100vh',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) },
  },
  contentInner: { paddingTop: 68 },
  avatar: {
    width: 36,
    height: 36,
    background: 'linear-gradient(135deg, #f472b6, #ec4899)',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 700,
  },
  userMeta: { padding: theme.spacing(1.5, 2) },
  userMetaName: { fontWeight: 700 },
  userMetaEmail: { color: theme.palette.text.secondary, fontSize: '0.8rem' },
}));

const navGroups = [
  {
    label: "Vue d'ensemble",
    items: [
      { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/admin' },
    ],
  },
  {
    label: 'Gestion',
    items: [
      { text: 'Utilisateurs', icon: <PeopleIcon />, path: '/admin/users' },
      { text: 'Entreprises', icon: <BusinessIcon />, path: '/admin/companies' },
      { text: 'Véhicules', icon: <DirectionsCarIcon />, path: '/admin/vehicles' },
      { text: 'Itinéraires', icon: <MapIcon />, path: '/admin/routes' },
      { text: 'Voyages', icon: <FlightIcon />, path: '/admin/trips' },
      { text: 'Réservations', icon: <BookIcon />, path: '/admin/bookings' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { text: 'Coupons', icon: <LocalOfferIcon />, path: '/admin/coupons' },
      { text: 'Commissions', icon: <MoneyIcon />, path: '/admin/commissions' },
      { text: 'Transactions', icon: <PaymentIcon />, path: '/admin/transactions' },
    ],
  },
  {
    label: 'Système',
    items: [
      { text: 'Vérifications', icon: <VerifiedUserIcon />, path: '/verification' },
      { text: 'Paramètres', icon: <SettingsIcon />, path: '/admin/settings' },
    ],
  },
];

const AdminLayout = ({ children, toggleDarkMode, darkMode }) => {
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();

  const handleDrawerToggle = () => setMobileOpen((s) => !s);
  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const userInitials = useMemo(() => {
    const email = (currentUser && currentUser.email) || '';
    const name = (currentUser && currentUser.displayName) || '';
    const src = name || email;
    return src ? src.charAt(0).toUpperCase() : 'A';
  }, [currentUser]);

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box>
      <Box className={classes.drawerToolbar}>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <FlightTakeoffIcon fontSize="small" />
          </Box>
          <Typography style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Voyage Admin
          </Typography>
        </Box>
      </Box>
      <Divider />
      {navGroups.map((group) => (
        <List
          key={group.label}
          subheader={
            <ListSubheader
              disableSticky
              className={classes.navSubheader}
              component="div"
            >
              {group.label}
            </ListSubheader>
          }
        >
          {group.items.map((item) => (
            <ListItem
              button
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
            >
              <ListItemIcon className={classes.navItemIcon}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                className={classes.navItemText}
                primary={item.text}
              />
            </ListItem>
          ))}
        </List>
      ))}
    </Box>
  );

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbarRow}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Box className={classes.brand}>
            <Box className={classes.brandIcon}>
              <FlightTakeoffIcon fontSize="small" />
            </Box>
            <Hidden xsDown>Voyage Admin</Hidden>
          </Box>

          <Box className={classes.search}>
            <Box className={classes.searchIcon}>
              <SearchIcon fontSize="small" />
            </Box>
            <InputBase
              placeholder="Rechercher…"
              className={classes.searchInput}
              inputProps={{ 'aria-label': 'rechercher' }}
            />
          </Box>

          <Box className={classes.spacer} />

          <Tooltip title={darkMode ? 'Mode clair' : 'Mode sombre'}>
            <IconButton onClick={toggleDarkMode} className={classes.iconWhite}>
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton className={classes.iconWhite}>
              <Badge color="secondary" variant="dot">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Compte">
            <Avatar
              className={classes.avatar}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              {userInitials}
            </Avatar>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            keepMounted
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box className={classes.userMeta}>
              <Typography className={classes.userMetaName}>
                {(currentUser && currentUser.displayName) || 'Administrateur'}
              </Typography>
              <Typography className={classes.userMetaEmail}>
                {(currentUser && currentUser.email) || ''}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                navigate('/admin/settings');
              }}
            >
              <SettingsIcon fontSize="small" style={{ marginRight: 8 }} />
              Paramètres
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToAppIcon fontSize="small" style={{ marginRight: 8 }} />
              Déconnexion
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Hidden mdUp implementation="js">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          {drawerContent}
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          variant="permanent"
          classes={{ paper: classes.drawerPaper }}
          open
          className={classes.drawer}
        >
          {drawerContent}
        </Drawer>
      </Hidden>

      <main className={classes.content}>
        <Box className={classes.contentInner}>{children}</Box>
      </main>
    </div>
  );
};

export default AdminLayout;
